import { forwardRef } from "react";
import { fileUrl } from "@/lib/api";
import { inr, fmtDate, fmtTime } from "@/lib/format";

const ORDER_LABEL = { DINE_IN: "Dine In", TAKEAWAY: "Takeaway", DELIVERY: "Delivery" };

export const ThermalInvoice = forwardRef(function ThermalInvoice({ bill, settings }, ref) {
  if (!bill) return null;
  const s = settings || {};
  const taxType = s.tax_type || "cgst_sgst";
  const pay = bill.payment || {};
  return (
    <div className="print-root" ref={ref}>
      <div className="thermal">
        <div style={{ textAlign: "center" }}>
          {s.logo_url && <img src={fileUrl(s.logo_url)} alt="" style={{ maxWidth: "40mm", maxHeight: "18mm", margin: "0 auto 4px" }} />}
          <div style={{ fontWeight: 700, fontSize: 15 }}>{s.restaurant_name}</div>
          {s.address && <div>{s.address}</div>}
          {s.phone && <div>Ph: {s.phone}</div>}
          {s.gstin && <div>GSTIN: {s.gstin}</div>}
          {s.fssai && <div>FSSAI: {s.fssai}</div>}
        </div>
        <div className="dashed" />
        <div>Bill: {bill.invoice_no}</div>
        <div>{fmtDate(bill.created_at)} {fmtTime(bill.created_at)}</div>
        <div>Type: {ORDER_LABEL[bill.order_type] || bill.order_type}{bill.table_name ? ` | ${bill.table_name}` : ""}</div>
        <div>Cashier: {bill.cashier_name}</div>
        {bill.customer_name && <div>Cust: {bill.customer_name}</div>}
        {bill.customer_phone && <div>Ph: {bill.customer_phone}</div>}
        <div className="dashed" />
        <table>
          <thead>
            <tr style={{ textAlign: "left" }}><th>Item</th><th style={{ textAlign: "center" }}>Qty</th><th style={{ textAlign: "right" }}>Rate</th><th style={{ textAlign: "right" }}>Amt</th></tr>
          </thead>
          <tbody>
            {bill.items.map((it, i) => (
              <tr key={i}>
                <td>{it.name}{it.variation ? ` (${it.variation})` : ""}</td>
                <td style={{ textAlign: "center" }}>{it.qty}</td>
                <td style={{ textAlign: "right" }}>{it.rate}</td>
                <td style={{ textAlign: "right" }}>{it.amount.toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="dashed" />
        <Row l="Subtotal" r={inr(bill.subtotal)} />
        {bill.discount_amount > 0 && <Row l="Discount" r={"-" + inr(bill.discount_amount)} />}
        <Row l="Taxable" r={inr(bill.taxable_amount)} />
        {taxType === "igst" ? (
          <Row l="IGST" r={inr(bill.igst)} />
        ) : (
          <>
            <Row l="CGST" r={inr(bill.cgst)} />
            <Row l="SGST" r={inr(bill.sgst)} />
          </>
        )}
        {bill.delivery_charge > 0 && <Row l="Delivery" r={inr(bill.delivery_charge)} />}
        <div className="dashed" />
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 14 }}>
          <span>TOTAL</span><span>{inr(bill.grand_total)}</span>
        </div>
        <div className="dashed" />
        <div>Payment: {pay.method}</div>
        {pay.method === "CASH" && pay.cash_received != null && (
          <>
            <Row l="Received" r={inr(pay.cash_received)} />
            <Row l="Change" r={inr(pay.change || 0)} />
          </>
        )}
        {pay.method === "SPLIT" && (pay.splits || []).map((sp, i) => <Row key={i} l={sp.method} r={inr(sp.amount)} />)}
        <div className="dashed" />
        <div style={{ textAlign: "center", marginTop: 4 }}>{s.invoice_footer}</div>
      </div>
    </div>
  );
});

function Row({ l, r }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span>{l}</span><span>{r}</span>
    </div>
  );
}

export const A4Invoice = forwardRef(function A4Invoice({ bill, settings }, ref) {
  if (!bill) return null;
  const s = settings || {};
  const taxType = s.tax_type || "cgst_sgst";
  const pay = bill.payment || {};
  return (
    <div className="print-root" ref={ref}>
      <div className="a4">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #111", paddingBottom: 16 }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            {s.logo_url && <img src={fileUrl(s.logo_url)} alt="" style={{ width: 64, height: 64, objectFit: "contain" }} />}
            <div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{s.restaurant_name}</div>
              <div style={{ color: "#444", fontSize: 12 }}>{s.address}</div>
              <div style={{ color: "#444", fontSize: 12 }}>Ph: {s.phone} {s.email ? `| ${s.email}` : ""}</div>
              {s.gstin && <div style={{ fontSize: 12 }}>GSTIN: {s.gstin}</div>}
              {s.fssai && <div style={{ fontSize: 12 }}>FSSAI: {s.fssai}</div>}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 1 }}>TAX INVOICE</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{bill.invoice_no}</div>
            <div style={{ fontSize: 12, color: "#444" }}>{fmtDate(bill.created_at)} {fmtTime(bill.created_at)}</div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", margin: "16px 0", fontSize: 12 }}>
          <div>
            <div><b>Order Type:</b> {ORDER_LABEL[bill.order_type] || bill.order_type}</div>
            {bill.table_name && <div><b>Table:</b> {bill.table_name}</div>}
            <div><b>Cashier:</b> {bill.cashier_name}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            {bill.customer_name && <div><b>Customer:</b> {bill.customer_name}</div>}
            {bill.customer_phone && <div><b>Phone:</b> {bill.customer_phone}</div>}
            {bill.customer_address && <div style={{ maxWidth: 240 }}><b>Address:</b> {bill.customer_address}</div>}
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f1f1f1" }}>
              <th style={{ textAlign: "left", padding: "8px 10px" }}>#</th>
              <th style={{ textAlign: "left", padding: "8px 10px" }}>Item</th>
              <th style={{ textAlign: "center", padding: "8px 10px" }}>Qty</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>Rate</th>
              <th style={{ textAlign: "center", padding: "8px 10px" }}>GST%</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.items.map((it, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "8px 10px" }}>{i + 1}</td>
                <td style={{ padding: "8px 10px" }}>{it.name}{it.variation ? ` (${it.variation})` : ""}</td>
                <td style={{ textAlign: "center", padding: "8px 10px" }}>{it.qty}</td>
                <td style={{ textAlign: "right", padding: "8px 10px" }}>{inr(it.rate)}</td>
                <td style={{ textAlign: "center", padding: "8px 10px" }}>{it.gst_percent}%</td>
                <td style={{ textAlign: "right", padding: "8px 10px" }}>{inr(it.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <div style={{ width: 280, fontSize: 13 }}>
            <ARow l="Subtotal" r={inr(bill.subtotal)} />
            {bill.discount_amount > 0 && <ARow l="Discount" r={"-" + inr(bill.discount_amount)} />}
            <ARow l="Taxable Amount" r={inr(bill.taxable_amount)} />
            {taxType === "igst" ? <ARow l="IGST" r={inr(bill.igst)} /> : <><ARow l="CGST" r={inr(bill.cgst)} /><ARow l="SGST" r={inr(bill.sgst)} /></>}
            {bill.delivery_charge > 0 && <ARow l="Delivery Charge" r={inr(bill.delivery_charge)} />}
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: "2px solid #111", marginTop: 8, paddingTop: 8, fontSize: 17, fontWeight: 800 }}>
              <span>Grand Total</span><span>{inr(bill.grand_total)}</span>
            </div>
            <div style={{ marginTop: 8, fontSize: 12 }}>Payment Method: <b>{pay.method}</b></div>
          </div>
        </div>

        <div style={{ marginTop: 40, textAlign: "center", color: "#444", fontSize: 12, borderTop: "1px solid #ddd", paddingTop: 12 }}>
          {s.invoice_footer}
        </div>
      </div>
    </div>
  );
});

function ARow({ l, r }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
      <span style={{ color: "#555" }}>{l}</span><span>{r}</span>
    </div>
  );
}
